import React from 'react';
import { useParams } from 'react-router-dom';
import { useBrandTagsQuery, usePurchaseOrderBrandsQuery } from 'api';
import { Col, FilterDrawer, Form, FormInstance, Input, Row, Select, TagSelect, Typography } from 'components';
import { ItemFilters } from './types';

interface ItemFiltersDrawerProps {
  form: FormInstance;
  visible: boolean;
  onClose: () => void;
  onApply: (values: ItemFilters) => void;
  onReset: () => void;
}

export const ItemFiltersDrawer = React.memo((props: ItemFiltersDrawerProps) => {
  const { form, visible, onClose, onApply, onReset } = props;
  const { id } = useParams() as { id: string };
  const { data: brandTagData = [] } = useBrandTagsQuery();
  const { data: brands = [] } = usePurchaseOrderBrandsQuery({ purchaseOrderId: id }, { skip: !id });

  // Handle form submission
  const handleFormSubmit = (values: any) => {
    // Map form values to API filter parameters
    const filters: ItemFilters = {};

    // Purchase price range
    if (values.purchasePriceMin) filters.purchasePriceMin = values.purchasePriceMin;
    if (values.purchasePriceMax) filters.purchasePriceMax = values.purchasePriceMax;

    // Sales price range
    if (values.salesPriceMin) filters.salesPriceMin = values.salesPriceMin;
    if (values.salesPriceMax) filters.salesPriceMax = values.salesPriceMax;

    // Margin range
    if (values.marginMin) filters.marginMin = values.marginMin;
    if (values.marginMax) filters.marginMax = values.marginMax;

    // Brand selection
    if (values.brandId) filters.brandId = values.brandId;

    // Brand tags
    if (values.brandTags && values.brandTags.length > 0) filters.brandTags = values.brandTags;
    onApply(filters);
  };

  // Clear all filters
  const clearFilters = () => {
    form.resetFields();
    onReset();
  };

  return (
    <FilterDrawer
      title="Filter Items"
      placement="left"
      width={471}
      styles={{
        body: {
          marginBottom: 80,
        },
      }}
      onClose={onClose}
      open={visible}
      onApply={() => form.submit()}
      onClear={clearFilters}
      onExpand={onClose}
    >
      <Form layout="vertical" form={form} onFinish={handleFormSubmit} initialValues={{}}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Row gutter={16} wrap={false} align={'bottom'}>
              <Col span={12}>
                <Form.Item name="purchasePriceMin" label={'Purchase Price Range'}>
                  <Input
                    placeholder={'Min'}
                    type={'number'}
                    min={0}
                    step={0.01}
                    suffix={<Typography.Text type={'secondary'}>€</Typography.Text>}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="purchasePriceMax">
                  <Input
                    placeholder={'Max'}
                    type={'number'}
                    min={0}
                    step={0.01}
                    suffix={<Typography.Text type={'secondary'}>€</Typography.Text>}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row gutter={16} wrap={false} align={'bottom'}>
              <Col span={12}>
                <Form.Item name="salesPriceMin" label={'Sales Price Range'}>
                  <Input
                    placeholder={'Min'}
                    type={'number'}
                    min={0}
                    step={0.01}
                    suffix={<Typography.Text type={'secondary'}>€</Typography.Text>}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="salesPriceMax">
                  <Input
                    placeholder={'Max'}
                    type={'number'}
                    min={0}
                    step={0.01}
                    suffix={<Typography.Text type={'secondary'}>€</Typography.Text>}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row gutter={16} wrap={false} align={'bottom'}>
              <Col span={12}>
                <Form.Item name="marginMin" label={'Margin Range'}>
                  <Input
                    placeholder={'Min'}
                    type={'number'}
                    min={0}
                    max={100}
                    step={0.1}
                    suffix={<Typography.Text type={'secondary'}>%</Typography.Text>}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="marginMax">
                  <Input
                    placeholder={'Max'}
                    type={'number'}
                    min={0}
                    max={100}
                    step={0.1}
                    suffix={<Typography.Text type={'secondary'}>%</Typography.Text>}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Form.Item name="brandId" label="Brand">
              <Select options={brands} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <TagSelect data={brandTagData} size={'middle'} name={'brandTags'} label={'Brand Tags'} />
          </Col>
        </Row>
      </Form>
    </FilterDrawer>
  );
});
